import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiCamera } from 'react-icons/fi';
import backofficeService from '../../../services/backofficeService';
import './PartnerForm.css';

const PartnerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    background_color: '#1a1a2e',
    href: '',
    description: '',
    text: '',
    order: 0,
    attrs: '',
    tags: '',
    benefits: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditing) {
      loadPartner();
    }
  }, [id]);

  const loadPartner = async () => {
    setIsLoading(true);
    try {
      const partner = await backofficeService.getPartner(id);
      setFormData({
        name: partner.name || '',
        logo: partner.logo || '',
        background_color: partner.background_color || '#1a1a2e',
        href: partner.href || '',
        description: partner.description || '',
        text: partner.text || '',
        order: partner.order || 0,
        attrs: Array.isArray(partner.attrs) ? partner.attrs.join(', ') : (partner.attrs || ''),
        tags: Array.isArray(partner.tags) ? partner.tags.join(', ') : (partner.tags || ''),
        benefits: Array.isArray(partner.benefits) ? partner.benefits.join(', ') : (partner.benefits || ''),
      });
      if (partner.logo) {
        setLogoPreview(partner.logo);
      }
    } catch (error) {
      setError('Erro ao carregar parceiro');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('href', formData.href);
      payload.append('description', formData.description);
      payload.append('text', formData.text);
      payload.append('order', parseInt(formData.order) || 0);
      payload.append('background_color', formData.background_color);
      
      // Add arrays as JSON
      const attrs = formData.attrs ? formData.attrs.split(',').map(s => s.trim()).filter(Boolean) : [];
      const tags = formData.tags ? formData.tags.split(',').map(s => s.trim()).filter(Boolean) : [];
      const benefits = formData.benefits ? formData.benefits.split(',').map(s => s.trim()).filter(Boolean) : [];
      
      attrs.forEach(attr => payload.append('attrs', attr));
      tags.forEach(tag => payload.append('tags', tag));
      benefits.forEach(benefit => payload.append('benefits', benefit));
      
      // Add logo file if new one was selected
      if (logoFile) {
        payload.append('logo', logoFile);
      }

      if (isEditing) {
        await backofficeService.updatePartner(id, payload, true);
      } else {
        await backofficeService.createPartner(payload, true);
      }
      navigate('/backoffice/partners');
    } catch (err) {
      setError(err.message || 'Erro ao guardar parceiro');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="backoffice-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>A carregar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="backoffice-page partner-form-page">
      <div className="page-header">
        <h1>{isEditing ? 'Editar Parceiro' : 'Novo Parceiro'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        {error && <div className="form-error">{error}</div>}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Nome *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Nome do parceiro"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="order">Ordem</label>
            <input
              type="number"
              id="order"
              name="order"
              value={formData.order}
              onChange={handleChange}
              min="0"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Logo</label>
            <div 
              className={`upload-area ${logoPreview ? 'has-image' : ''}`}
              onClick={() => document.getElementById('logo').click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove('drag-over'); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
                const file = e.dataTransfer.files[0];
                if (file && file.type.startsWith('image/')) {
                  setLogoFile(file);
                  const reader = new FileReader();
                  reader.onloadend = () => setLogoPreview(reader.result);
                  reader.readAsDataURL(file);
                }
              }}
            >
              <input
                type="file"
                id="logo"
                name="logo"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {logoPreview ? (
                <div className="upload-preview">
                  <img src={logoPreview} alt="Logo preview" />
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLogoFile(null);
                      setLogoPreview('');
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <div className="upload-icon"><FiCamera size={32} /></div>
                  <p className="upload-text">Arrasta uma imagem ou clica para selecionar</p>
                  <span className="upload-hint">PNG, JPG ou GIF (máx. 2MB)</span>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="background_color">Cor de Fundo da Imagem</label>
            <div className="color-picker-wrapper">
              <div className="color-picker-row">
                <input
                  type="color"
                  id="background_color"
                  name="background_color"
                  value={formData.background_color}
                  onChange={handleChange}
                  className="color-picker-input"
                />
                <input
                  type="text"
                  value={formData.background_color}
                  onChange={handleChange}
                  name="background_color"
                  className="color-picker-text"
                  placeholder="#1a1a2e"
                />
              </div>
              <div 
                className="color-preview" 
                style={{ backgroundColor: formData.background_color }}
              >
                {logoPreview && <img src={logoPreview} alt="Preview" />}
              </div>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="href">Website / Link Referral</label>
            <input
              type="url"
              id="href"
              name="href"
              value={formData.href}
              onChange={handleChange}
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="description">Descrição Curta</label>
            <input
              type="text"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Ex: Casino, Apostas Desportivas..."
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="text">Texto Completo</label>
            <textarea
              id="text"
              name="text"
              value={formData.text}
              onChange={handleChange}
              placeholder="Descrição detalhada do parceiro..."
              rows="5"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="attrs">Atributos (separados por vírgula)</label>
            <input
              type="text"
              id="attrs"
              name="attrs"
              value={formData.attrs}
              onChange={handleChange}
              placeholder="Ex: Licenciado, Seguro, Rápido"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="tags">Tags (separadas por vírgula)</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="Ex: casino, slots, apostas"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="benefits">Benefícios (separados por vírgula)</label>
            <input
              type="text"
              id="benefits"
              name="benefits"
              value={formData.benefits}
              onChange={handleChange}
              placeholder="Ex: Bónus de boas-vindas, Cashback, Free Spins"
            />
          </div>
        </div>

        <div className="form-actions">
          <Link to="/backoffice/partners" className="btn-secondary">
            Cancelar
          </Link>
          <button type="submit" className="btn-primary" disabled={isSaving}>
            {isSaving ? 'A guardar...' : (isEditing ? 'Guardar Alterações' : 'Criar Parceiro')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PartnerForm;
